import { useGetAlertaUsuariosInfo, useGroupNotificationsByDate, useMarcarAlertasLeidas } from "@/shared/hooks/useNotificaciones.hook";
import { useUserData } from "@/shared/hooks/useUserData.hook";
import { StorageKeys } from "@/shared/types/enums/Storage.enum";
import type { PerfilEntidad } from "@/shared/types/interfaces/user.interface";
import { storage } from "@/shared/utils/Const.utils";
import { Box, Button, CircularProgress, Divider, IconButton, Tab, Tabs, Tooltip } from "@mui/material";
import { RiCalendarLine, RiCheckDoubleLine, RiCloseLine, RiInformationLine } from "@remixicon/react";
import { useEffect, useRef, useState, type FunctionComponent } from "react";
import customAlert from "../CustomAlert";
import type { DataLeida, DataNoLeida, GetAlertaUsuariosInfoResponse } from "@/shared/types/interfaces/ApiConsultaGestionCasos/Actividades.interface";
import TableEmptyState from "../EmptyStates/TableEmptyState";
import ItemNotificacion from "./ItemNotificacion";
import { useTranslation } from "react-i18next";

interface NotificacionesProps {
    isOpenNotificaciones: boolean;
    handleNotificaciones : () => void;
    existenAlertas: boolean;
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            className="flex flex-col flex-grow overflow-hidden h-full"
            {...other}
        >
            {value === index && <Box className="flex-grow overflow-y-auto">{children}</Box>}
        </div>
    );
}

const Notificaciones: FunctionComponent<NotificacionesProps> = ({
    handleNotificaciones, isOpenNotificaciones, existenAlertas
}) => {
    const { t } = useTranslation();
    const menuRef = useRef<HTMLElement>(null);
    const perfil : PerfilEntidad = storage.get(StorageKeys.PERFIL);
    const { userData } = useUserData();
    const idUsuarioLogeado = userData?.id!
    const idRelUsuarioEntidades = perfil.infoEntidad?.relacion.idRelUsuarioEntidades;
    const idRol = perfil.infoEntidad?.relacion.idRol;
    const [tabValue, setTabValue] = useState(0);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const {
        data: dataGetAlertas,
        isLoading: isLoadingGetAlertas
    } = useGetAlertaUsuariosInfo({
        idUsuarioLogeado,
        top: 40,
        visto: 0,
        idRelUsuarioEntidades,
        idRol
    });

    const {
        mutate: marcarAlertasLeidas, isPending: isPendingMarcarAlertasLeidas
    } = useMarcarAlertasLeidas();
    
    function obtenerIdsAlertasNoLeidas(alertas: GetAlertaUsuariosInfoResponse): number[] {
        const idsAlertasUsuarios: number[] = alertas.dataNoLeidas.map(alerta => alerta.IdAlertaUsuarios);
        return idsAlertasUsuarios;
    }

    const handleReadAlerts = (data: GetAlertaUsuariosInfoResponse | undefined) => {
        if(!data) return;

        const listAlerts = obtenerIdsAlertasNoLeidas(data);

        marcarAlertasLeidas(
            {
                idAlertaUsuarios: listAlerts,
                idUsuario: idUsuarioLogeado
            },
            {
                onError: (err) => {
                    console.log(err)
                    customAlert({
                        icon: 'error',
                        toast: true,
                        titulo: err.message || 'Error al marcar alertas leídas',
                    });
                },
                onSuccess: (res) => {
                    console.log(res)
                    /* customAlert({
                        icon: 'success',
                        titulo: `Alertas leídas con exito!`,
                        tiempo: 8,
                        toast: true,
                    }); */
                },
            }
        )
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                const target = event.target as HTMLElement;
                if (!target.closest('[data-menu-trigger]')) {
                    handleNotificaciones();
                }
            }
        };

        if (isOpenNotificaciones) {
            setTimeout(() => {
                document.addEventListener('mousedown', handleClickOutside);
            }, 100);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpenNotificaciones, handleNotificaciones]);

    const alertasNoLeidasAgrupadas = useGroupNotificationsByDate(dataGetAlertas?.dataNoLeidas || []);
    const alertasLeidasAgrupadas = useGroupNotificationsByDate(dataGetAlertas?.dataLeidas || []);

    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 600);
        };

        checkMobile(); // Verificar al montar
        window.addEventListener('resize', checkMobile);

        return () => {
            window.removeEventListener('resize', checkMobile);
        };
    }, []);

    // Cerrar menú al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!isMobile && menuRef.current && !menuRef.current.contains(event.target as Node)) {
                // Verificar si el clic no es en el botón que abre el menú (el avatar)
                const target = event.target as HTMLElement;
                if (!target.closest('[data-menu-trigger]')) {
                    handleNotificaciones();
                }
            }
        };

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMobile, handleNotificaciones]);

    return (
        <>
        <section 
            ref={menuRef} 
            className="
                        fixed top-0 right-0 left-0 bottom-0 
                        w-full h-full bg-white z-50
                        rounded-none shadow-none
                        overflow-y-auto
                        md:absolute 
                        md:w-[450px] md:h-auto md:max-h-[80vh]
                        md:rounded-lg md:shadow-xl md:z-10
                        md:top-18 md:right-10
                        md:left-auto
                        md:overflow-y-auto
                        border border-[#8e8e914d]
                    "
        >
            <div className="flex flex-col h-full">
                <div className="flex gap-2 items-center px-4 pb-2 pt-4 text-[#0050da] border-b md:border-b-0">
                    <h4 className="h4 font-Bold text-lg md:text-xl">{t('notifications.alerts')}</h4>
                    <Tooltip title={`${t('notifications.sectionAllowsViewingCaseAlerts')}`}>
                        <RiInformationLine size={24} />
                    </Tooltip>
                    <div className='flex-grow' />
                    {isMobile && <button onClick={handleNotificaciones} className="hover:cursor-pointer"><RiCloseLine size={24} /></button>}
                </div>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={handleChange} aria-label="basic tabs example" variant="fullWidth">
                        <Tab label={`${t('notifications.unread')}`} {...a11yProps(0)} />
                        <Tab label={`${t('notifications.read')}`} {...a11yProps(1)} />
                    </Tabs>
                </Box>
                {
                    isLoadingGetAlertas 
                    ? 
                    <div className="text-[#5E5D62] font-Bold py-1 px-4 py-2 flex gap-2 items-center">
                        <CircularProgress size={24} color="inherit" />
                        <h4>{`${t('app.loading')}`}</h4>
                    </div>
                    :
                    <>
                    <CustomTabPanel value={tabValue} index={0}>
                        {
                            dataGetAlertas?.dataNoLeidas.length === 0
                            ? <TableEmptyState title={`${t('notifications.noUnreadAlertsToShow')}`} text={`${t('notifications.noAlertsAvailable')}`} buttonText=""/>
                            :
                            <div className="flex flex-col h-full">
                                <div className="flex-grow overflow-y-auto">
                                    <ul className="flex flex-col">
                                        {Object.entries(alertasNoLeidasAgrupadas).map(([fecha, alertas]) => (
                                            <li key={fecha}>
                                                <p className="text-sm font-bold text-[#5E5D62] font-Bold py-1 sticky top-0 bg-white px-4 py-2 flex gap-2 items-center">
                                                    {<RiCalendarLine/>}
                                                    {fecha}
                                                </p>
                                                {alertas.map((not: DataNoLeida, index: number) => (
                                                    <div key={not.IdAlertaUsuarios}>
                                                    <ItemNotificacion
                                                        TipoAlerta={not.TipoAlerta}
                                                        Alerta={not.Alerta}
                                                        FechaAlerta={not.FechaAlerta}
                                                        TipoActividad={not.IdTipoActividad}
                                                        IdCaso={not.IdCaso}
                                                        IdTramite={not.IdTramite}
                                                        idAlertaUsuarios={not.IdAlertaUsuarios}
                                                        idUsuario={idUsuarioLogeado}
                                                        IsReaded={false}
                                                        handleNotificaciones={handleNotificaciones}
                                                    />
                                                    {index < alertas.length - 1 && (
                                                        <Divider className="my-2" />
                                                    )}
                                                    </div>
                                                ))}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <Divider/>
                                <div className="flex m-2 flex-shrink-0" hidden={!existenAlertas}>
                                    <Tooltip title={`${t('notifications.markAllAlertsAsRead')}`} arrow>    
                                        <Button variant="text" endIcon={<RiCheckDoubleLine />} onClick={() => customAlert({
                                            icon: 'question',
                                            titulo: `${t('notifications.markAlertsAsRead')}`,
                                            mensaje: `${t('notifications.allAlertsWillBeMarkedAsRead')}`,
                                            botonCancelar: true,
                                            textoBotonAceptar: `${t('actions.yes')}`,
                                            textoBotonCancelar: `${t('actions.no')}`,
                                            colorBotonAceptar: '#3085d6',
                                            colorBotonCancelar: '#d33',
                                            callback: (result) => {
                                                if (result) {
                                                    console.log('si')
                                                    handleReadAlerts(dataGetAlertas)
                                                }
                                            }
                                        })}>
                                            {t('notifications.markAsRead')}
                                        </Button>
                                    </Tooltip>
                                </div>
                            </div>
                        }
                    </CustomTabPanel>
                    <CustomTabPanel value={tabValue} index={1}>
                        {
                            dataGetAlertas?.dataLeidas.length === 0
                            ? <TableEmptyState title={t('notifications.noReadAlertsToShow')} text={t('notifications.noAlertsAvailable')} buttonText=""/>
                            :
                            <div className="max-h-[65vh] overflow-y-auto md:max-h-[50vh]">
                                <ul className="flex flex-col">
                                    {Object.entries(alertasLeidasAgrupadas).map(([fecha, alertas]) => (
                                        <li key={fecha}>
                                            <p className="text-sm font-bold text-[#5E5D62] font-Bold py-1 sticky top-0 bg-white px-4 py-2 flex gap-2 items-center">
                                                {<RiCalendarLine/>}
                                                {fecha}
                                            </p>
                                            {alertas.map((not: DataLeida, index: number) => (
                                                <div key={not.IdAlertaUsuarios}>
                                                    <ItemNotificacion
                                                        TipoAlerta={not.TipoAlerta}
                                                        Alerta={not.Alerta}
                                                        FechaAlerta={not.FechaAlerta}
                                                        TipoActividad={not.IdTipoActividad}
                                                        IdCaso={not.IdCaso}
                                                        IdTramite={not.IdTramite}
                                                        idAlertaUsuarios={not.IdAlertaUsuarios}
                                                        idUsuario={idUsuarioLogeado}
                                                        IsReaded={true}
                                                        handleNotificaciones={handleNotificaciones}
                                                    />
                                                    {index < alertas.length - 1 && (
                                                        <Divider className="my-2" />
                                                    )}
                                                </div>
                                            ))}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        }
                    </CustomTabPanel>
                    </>
                }
            </div>
        </section>
        </>
    );
}

export default Notificaciones;